import React, { useRef, useState } from 'react';
import { UploadCloud, Film, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export function RawVideoUploader({ onVideoUploaded, currentVideoUrl }) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Límite de 40MB
    if (file.size > 40 * 1024 * 1024) {
      setUploadError('El video excede el límite de 40MB');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result;
        try {
          const res = await fetch('/api/video/upload-raw', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              videoBase64: base64Data,
              fileName: file.name
            })
          });
          const data = await res.json();
          if (data.success && data.videoUrl) {
            setSuccessInfo(`Cargado: ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`);
            onVideoUploaded(data.videoUrl, file.name);
          } else {
            // Fallback a blob URL local
            const localBlob = URL.createObjectURL(file);
            setSuccessInfo(`Cargado local: ${file.name}`);
            onVideoUploaded(localBlob, file.name);
          }
        } catch {
          const localBlob = URL.createObjectURL(file);
          setSuccessInfo(`Cargado local: ${file.name}`);
          onVideoUploaded(localBlob, file.name);
        } finally {
          setIsUploading(false);
        }
      };
      reader.onerror = () => {
        setUploadError('Error leyendo el archivo de video');
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setUploadError(err.message || 'Error al procesar video');
      setIsUploading(false);
    }
  };

  return (
    <div style={{
      background: '#141724',
      border: '1px dashed rgba(168, 85, 247, 0.4)',
      borderRadius: '10px',
      padding: '14px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      transition: 'all 0.2s'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ background: 'rgba(168, 85, 247, 0.15)', padding: '6px', borderRadius: '6px', color: '#c084fc' }}>
            <Film size={16} />
          </div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc' }}>
              Subir Video RAW Propio
            </div>
            <div style={{ fontSize: '10px', color: '#94a3b8' }}>
              Graba con celular o cámara (.mp4, .mov, .webm)
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          style={{
            background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
            border: 'none',
            color: '#fff',
            borderRadius: '6px',
            padding: '6px 12px',
            fontSize: '11px',
            fontWeight: 700,
            cursor: isUploading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          {isUploading ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
          {isUploading ? 'Subiendo...' : 'Seleccionar Video'}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>

      {uploadError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', fontSize: '11px' }}>
          <AlertCircle size={13} /> {uploadError}
        </div>
      )}

      {successInfo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981', fontSize: '11px' }}>
          <CheckCircle2 size={13} /> {successInfo}
        </div>
      )}
    </div>
  );
}
